import { Allocation, EnergyCompany } from "@/api/SolarCloudApi";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SegmentedNumericInput } from "@/components/ui/SegmentedNumericInput";
import { Button } from "@/components/ui/Button";
import type { AllocationForm } from "./AddAllocationForm";
import { allocationFormSchema } from "./AddAllocationForm";
import LinkCard from "./LinkCard";
import { cn } from "@/util/cn";
import IntegerInput from "@/components/ui/IntegerInput";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover2";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/Command";
import { useState, useEffect } from "react";

// Types
interface AllocationFormProps {
  energyCompanies: EnergyCompany[];
  unallocatedWatts: number;
  account: "gas" | "electricity" | "bank" | undefined;
  onSubmit: (allocation: Allocation) => void;
  onCancel?: () => void;
  defaultValues?: Partial<AllocationForm>;
  isUpdate?: boolean;
  paymentType?: "bank" | "energyCompany" | "paypal" | "none"; 
}

// Default values for each payment type
const bankDefault: AllocationForm = {
  paymentType: "bank",
  nickname: "",
  wattCapacity: 0,
  accountName: "",
  bsb: "",
  accountNumber: "",
};

const energyCompanyDefault: AllocationForm = {
  paymentType: "energyCompany",
  nickname: "",
  wattCapacity: 0,
  energyCompany: {
    uuid: "",
    name: "",
  },
  otherName: "",
  customerReferenceNumber: "",
  billerCode: "",
};

const paypalDefault: AllocationForm = {
  paymentType: "paypal",
  nickname: "",
  wattCapacity: 0,
  paypalEmail: "",
};

const noneDefault: AllocationForm = {
  paymentType: "none",
  nickname: "",
  wattCapacity: 0,
};

export default function AllocationForm({
  energyCompanies,
  unallocatedWatts,
  account,
  onSubmit,
  onCancel,
  defaultValues,
  isUpdate = false,
}: AllocationFormProps) {
  const [open, setOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const defaultValuesMap: Record<
    AllocationForm["paymentType"],
    AllocationForm
  > = {
    none: noneDefault,
    bank: bankDefault,
    energyCompany: energyCompanyDefault,
    paypal: paypalDefault,
  };

  const form = useForm<AllocationForm>({
    resolver: zodResolver(allocationFormSchema),
    defaultValues: defaultValues || defaultValuesMap.none,
  });

  const paymentType = form.watch("paymentType"); 

  useEffect(() => {
    if (account === "bank") {
      form.setValue("paymentType", "bank");
      return;
    }

    if (account === "gas" || account === "electricity") {
      form.setValue("paymentType", "energyCompany");
      return;
    }

    if (isUpdate && defaultValues?.paymentType === "bank") {
      form.setValue("paymentType", "bank");
    }
  }, [account, form, isUpdate, defaultValues]);

  const handleFormSubmit = (data: AllocationForm) => {
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    let allocation: Allocation = {};
    switch (data.paymentType) {
      case "bank":
        allocation = {
          ...data,
          bankPayee: {
            accountName: data.accountName,
            bsb: data.bsb,
            accountNumber: data.accountNumber,
          },
        };
        break;
      case "energyCompany":
        if (account === "gas") {
          data.nickname = `::gas::${data.nickname}`;
        }
        allocation = {
          ...data,
          energyCompanyPayee: {
            energyCompany: energyCompanies.find(
              (company) => company.uuid === data.energyCompany.uuid
            ),
            otherName: data.energyCompany.name.toLowerCase() === "other" ? data.otherName : undefined,
            customerReferenceNumber: data.customerReferenceNumber,
            billerCode: data.billerCode,
          },
        };
        break;
      case "paypal":
        allocation = {
          ...data,
          payPalPayee: {
            email: data.paypalEmail,
          },
        };
        break;
      default:
        throw new Error("Invalid payment type");
    }

    onSubmit(allocation);
  };

  const handleBackToEdit = () => {
    setShowConfirmation(false);
  };

  if (showConfirmation) {
    const formValues = form.getValues();
    return (
      <div className="flex flex-col gap-6">
        <h3 className="font-semibold">Please confirm your details</h3>
        <LinkCard
          formValues={formValues}
          account={account}
          showBpayWarning={true}
        />

        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            type="button"
            onClick={handleBackToEdit}
            className="h-full"
          >
            Back to Edit
          </Button>
          <Button
            type="button"
            onClick={() => form.handleSubmit(handleFormSubmit)()}
            disabled={form.formState.isSubmitting}
            className="py-6 flex-1 capitalize"
          >
            Confirm
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)}>
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="nickname"
            render={({ field }) => (
              <FormItem>
                {!isUpdate && (
                  <FormDescription>
                    Give the link a name e.g Home or Apartment or Daughters{" "}
                    <span className="capitalize">{account}</span> Bill
                  </FormDescription>
                )}

                <FormLabel>Account Nickname</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter a nickname"
                    {...field}
                    className="py-6 bg-brand-neutral-700"
                    maxLength={40}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="wattCapacity"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>
                    Panels: {(form.watch("wattCapacity") || 0) / 100} (
                    {(form.watch("wattCapacity") || 0) / 1000 + "kW"})
                  </FormLabel>
                  <FormDescription className="flex justify-center">
                    <span className="min-w-3">
                      {unallocatedWatts / 100 - field.value / 100}
                    </span>
                    {unallocatedWatts / 100 - field.value / 100 === 1 &&
                      "panel remaining"}
                    {unallocatedWatts / 100 - field.value / 100 !== 1 &&
                      "panels remaining"}
                  </FormDescription>
                </div>

                <FormControl>
                  <IntegerInput
                    value={field.value / 100}
                    setValue={(value) => {
                      form.setValue("wattCapacity", value * 100);
                    }}
                    max={unallocatedWatts / 100}
                    buttonClassName="bg-brand-neutral-700 border border-input h-[50px] px-6 disabled:opacity-80 hover:bg-brand-neutral-600/50"
                    inputClassName="bg-brand-neutral-700 border border-input h-[50px] rounded-md sm:rounded-none"
                    className="w-full sm:w-full md:w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {(account === "bank" || (isUpdate && defaultValues?.paymentType === "bank")) ? (
            <>
              <input type="hidden" {...form.register("paymentType")} value="bank" />
            </>
          ) : account === "gas" || account === "electricity" ? (
            <>
              <input
                type="hidden"
                {...form.register("paymentType")}
                value="energyCompany"
              />
            </>
          ) : (
            <FormField
              control={form.control}
              name="paymentType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Type</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      className="w-full py-3 bg-brand-neutral-700 rounded-md"
                    >
                      <option value="none">None</option>
                      <option value="bank">Bank</option>
                      <option value="energyCompany">Energy Company</option>
                      <option value="paypal">PayPal</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {paymentType === "energyCompany" && (
            <>
              <FormField
                control={form.control}
                name="energyCompany.name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Energy Company</FormLabel>
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          role="combobox"
                          className={cn(
                            "w-full justify-between py-6 bg-brand-neutral-700 border font-normal hover:bg-brand-neutral-600/50 focus:ring-gray-700 focus:ring-offset-0 focus:ring-2 focus:outline-none focus-visible:ring-offset-0",
                            !field.value && "text-brand-neutral-500"
                          )}
                        >
                          {field.value
                            ? energyCompanies.find(
                                (company) => company.name === field.value
                              )?.name
                            : "Select energy company"}
                          <ChevronsUpDown className="opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-full">
                        <Command>
                          <CommandInput placeholder="Search Energy Companies..." />
                          <CommandList>
                            <CommandEmpty>
                              No energy company found.
                            </CommandEmpty>
                            <CommandGroup>
                              {energyCompanies
                                .filter(
                                  (company) =>
                                    company.name?.toLowerCase() !== "other"
                                )
                                .map((company) => (
                                  <CommandItem
                                    key={company.uuid}
                                    value={company.name!}
                                    className=""
                                    onSelect={() => {
                                      form.setValue(
                                        "energyCompany.name",
                                        company.name!
                                      );
                                      form.setValue(
                                        "energyCompany.uuid",
                                        company.uuid!
                                      );
                                      setOpen(false);
                                    }}
                                  >
                                    {company.name}
                                    <Check
                                      className={cn(
                                        "ml-auto",
                                        company.name === field.value
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                          <CommandSeparator />
                          <CommandGroup>
                            <CommandItem
                              value="other"
                              className=""
                              onSelect={() => {
                                const otherCompany = energyCompanies.find(
                                  (company) =>
                                    company.name?.toLowerCase() === "other"
                                );
                                if (otherCompany) {
                                  form.setValue(
                                    "energyCompany.name",
                                    otherCompany.name!
                                  );
                                  form.setValue(
                                    "energyCompany.uuid",
                                    otherCompany.uuid!
                                  );
                                }
                                setOpen(false);
                              }}
                            >
                              Other
                              <Check
                                className={cn(
                                  "ml-auto",
                                  field.value?.toLowerCase() === "other"
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("energyCompany.name")?.toLowerCase() === "other" && (
                <FormField
                  control={form.control}
                  name="otherName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Other Energy Company Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter energy company name"
                          {...field}
                          className="py-6 bg-brand-neutral-700"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <p className="text-sm text-muted-foreground mt-1">
                Please enter your BPAY details below:
              </p>
              <FormField
                control={form.control}
                name="billerCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Biller Code</FormLabel>
                    <FormControl>
                      <SegmentedNumericInput
                        maxLength={10}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="customerReferenceNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer Reference Number</FormLabel>
                    <FormControl>
                      <SegmentedNumericInput
                        maxLength={20}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {paymentType === "bank" && (
            <>
              <FormField
                control={form.control}
                name="accountName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Account name"
                        {...field}
                        className="py-6 bg-brand-neutral-700"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bsb"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>BSB</FormLabel>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        pattern={REGEXP_ONLY_DIGITS}
                        {...field}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} className="bg-brand-neutral-700 h-12 w-12 text-lg" />
                          <InputOTPSlot index={1} className="bg-brand-neutral-700 h-12 w-12 text-lg" />
                          <InputOTPSlot index={2} className="bg-brand-neutral-700 h-12 w-12 text-lg" />
                          <InputOTPSlot index={3} className="bg-brand-neutral-700 h-12 w-12 text-lg" />
                          <InputOTPSlot index={4} className="bg-brand-neutral-700 h-12 w-12 text-lg" />
                          <InputOTPSlot index={5} className="bg-brand-neutral-700 h-12 w-12 text-lg" />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="accountNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Number</FormLabel>
                    <FormControl>
                      <SegmentedNumericInput
                        maxLength={10}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {paymentType === "paypal" && (
            <FormField
              control={form.control}
              name="paypalEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>PayPal Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="PayPal email"
                      {...field}
                      className="py-6 bg-brand-neutral-700"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="flex justify-between gap-2">
            {onCancel && (
              <Button
                variant="outline"
                type="button"
                onClick={onCancel}
                className="py-6"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              className={cn("py-6 capitalize", !isUpdate && "flex-1")}
            >
              {isUpdate ? `Save ${account} Link` : `Create ${account} Link`}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
